import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import Table from '../common/Table';
import { GridToolbarContainer } from '@mui/x-data-grid';
import ProjectStatusDisplay from '../projects/ProjectStatusDisplay';
import { ProjectFolder } from '../../data/models/company';
import { Button, IconButton, Menu, MenuItem, useTheme } from '@mui/material';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import EditIcon from '@mui/icons-material/Edit';
import { updateCompanyProjectFolder } from '../../services/company-service';
import { useDispatch } from 'react-redux';
import {
	asyncCallStart,
	asyncCallDone,
	asyncCallError,
	refreshCompanySuccess,
} from '../../data/stores/companyStore';
import { showAlert } from '../../data/stores/alertStore';
import AddIcon from '@mui/icons-material/Add';
import styled from 'styled-components';
import ProjectFolderCreationModal from '../project-types/FolderCreationModal';
import EditProjectFolderModal from './EditProjectFolderModal';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

// Extended interface to support additional properties needed for folder tree display
interface FolderWithPath extends Omit<ProjectFolder, 'subFolders'> {
	path?: string[];
	folderPath?: string[];
	parentFolderId?: string | null;
	subFolders?: FolderWithPath[];
}

export default function CompanyProjectFolders({
	projectFolders = [],
	initialized = false,
}: {
	projectFolders: ProjectFolder[];
	initialized: boolean;
}) {
	const dispatch = useDispatch();
	const theme = useTheme();
	const [sortModel, setSortModel] = useState<GridSortModel>([
		{
			field: 'display',
			sort: 'asc',
		},
	]);
	const [isSubFolderModalOpen, setIsSubFolderModalOpen] = useState(false);
	const [selectedFolderPath, setSelectedFolderPath] = useState<string[]>([]);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const [folderToEdit, setFolderToEdit] = useState<{
		id: string;
		name: string;
		folderPath?: string[];
	} | null>(null);

	useEffect(() => {}, []);

	const handleArchive = async (id: string, folderPath: string[] = []) => {
		dispatch(asyncCallStart());
		try {
			const data = {
				id,
				active: false,
				folderPath: folderPath.length > 0 ? folderPath : undefined,
			};
			const response = await updateCompanyProjectFolder(data);
			dispatch(refreshCompanySuccess(response));
			dispatch(asyncCallDone());
			dispatch(
				showAlert({
					message: `${
						folderPath.length > 0 ? 'Sub folder' : 'Project folder'
					} archived`,
					severity: 'success',
				})
			);
		} catch (error) {
			console.log('error', error);
			dispatch(asyncCallError(error));
		}
	};

	const handleUnarchive = async (id: string, folderPath: string[] = []) => {
		dispatch(asyncCallStart());
		try {
			const data = {
				id,
				active: true,
				folderPath: folderPath.length > 0 ? folderPath : undefined,
			};
			const response = await updateCompanyProjectFolder(data);
			dispatch(refreshCompanySuccess(response));
			dispatch(asyncCallDone());
			dispatch(
				showAlert({
					message: `${
						folderPath.length > 0 ? 'Sub folder' : 'Project folder'
					} unarchived`,
					severity: 'success',
				})
			);
		} catch (error) {
			console.log('error', error);
			dispatch(asyncCallError(error));
		}
	};

	// Function to find a folder and its subfolders in the tree by path
	const findFolderByPath = (
		folders: FolderWithPath[],
		path: string[]
	): FolderWithPath | null => {
		if (!path.length) return null;

		const [currentId, ...remainingPath] = path;
		const folder = folders.find((f) => f.id === currentId);

		if (!folder) return null;

		if (!remainingPath.length) return folder;

		if (folder.subFolders && folder.subFolders.length) {
			return findFolderByPath(folder.subFolders, remainingPath);
		}

		return null;
	};

	const handleCreateSubFolder = (folderPath: string[]) => {
		setSelectedFolderPath(folderPath);
		setIsSubFolderModalOpen(true);
	};

	const handleEditFolder = (
		id: string,
		name: string,
		folderPath: string[] = []
	) => {
		setFolderToEdit({
			id,
			name,
			folderPath: folderPath.length > 0 ? folderPath : undefined,
		});
		setIsEditModalOpen(true);
	};

	// Function to determine if a row is a sub-folder based on its path
	const isSubfolder = (path: string[] | undefined) => {
		return path && path.length > 1;
	};

	const columns: GridColDef[] = useMemo(() => {
		return [
			{
				field: 'active',
				headerName: 'Active',
				minWidth: 150,
				disableColumnMenu: true,
				sortable: true,
				renderCell: (params: any) => (
					<div style={{ opacity: params.row.active ? 1 : 0.5 }}>
						<ProjectStatusDisplay
							status={!!params.value ? 'ACTIVE' : 'ARCHIVED'}
						/>
					</div>
				),
			},
			{
				field: 'createdBy',
				headerName: 'Created By',
				minWidth: 200,
				disableColumnMenu: true,
				sortable: true,
				renderCell: (params: any) => (
					<div style={{ opacity: params.row.active ? 1 : 0.5 }}>
						{params.value?.name}
					</div>
				),
			},
			{
				field: 'created',
				headerName: 'Created On',
				minWidth: 140,
				disableColumnMenu: true,
				sortable: true,
				renderCell: (params: any) => (
					<div style={{ opacity: params.row.active ? 1 : 0.5 }}>
						{dayjs(params.value).format('MMM D, h:mm A')}
					</div>
				),
			},
			{
				field: 'actions',
				headerName: 'Action',
				minWidth: 80,
				disableColumnMenu: true,
				sortable: false,
				headerAlign: 'center',
				align: 'center',
				renderCell: (params: any) => {
					// Don't show action buttons for auto-generated rows (groups)
					if (params.row.type === 'group') {
						return null;
					}

					return (
						<RowActionMenu
							row={params.row}
							onEditFolder={handleEditFolder}
							onCreateSubFolder={handleCreateSubFolder}
							onArchive={handleArchive}
							onUnarchive={handleUnarchive}
						/>
					);
				},
			},
		];
	}, []);

	// Enhanced recursive function to transform folder data for the tree structure
	const processFolderForTree = (
		folder: ProjectFolder,
		parentPath: string[] = []
	): FolderWithPath => {
		const currentPath = [...parentPath, folder.id];

		// Create a processed folder with the path info
		const processedFolder: FolderWithPath = {
			...folder,
			path: currentPath,
			parentFolderId:
				parentPath.length > 0
					? parentPath[parentPath.length - 1]
					: null,
			folderPath: parentPath,
			// We preserve the subFolders array but process each item
			subFolders: folder.subFolders
				? folder.subFolders.map((subFolder) =>
						processFolderForTree(subFolder, currentPath)
				  )
				: undefined,
		};

		return processedFolder;
	};

	// Process all folders to add path information while preserving hierarchy
	const processAllFolders = (folders: ProjectFolder[]): FolderWithPath[] => {
		return folders.map((folder) => processFolderForTree(folder));
	};

	// Flatten the folder hierarchy for the MUI DataGrid while preserving tree structure
	const flattenFolders = (folders: FolderWithPath[]): FolderWithPath[] => {
		if (!folders || !Array.isArray(folders)) return [];

		const result: FolderWithPath[] = [];

		// Recursive function to add all folders and their subfolders to the result array
		const addToResult = (folderArray: FolderWithPath[]) => {
			folderArray.forEach((folder) => {
				// Add folder to result
				result.push(folder);

				// Process subfolders if they exist
				if (folder.subFolders && folder.subFolders.length > 0) {
					addToResult(folder.subFolders);
				}
			});
		};

		// Start the recursion
		addToResult(folders);

		return result;
	};

	// Path getter for tree data
	const getTreeDataPath = (row: FolderWithPath) => {
		return row.path || [row.id];
	};

	// Check if a folder has any subfolders (used to decide if to display dropdown)
	const hasFolderSubfolders = (
		folderId: string,
		allFolders: FolderWithPath[]
	): boolean => {
		return allFolders.some((folder) => folder.parentFolderId === folderId);
	};

	const rows: FolderWithPath[] = useMemo(() => {
		if (!projectFolders || !Array.isArray(projectFolders)) {
			return [];
		}

		// First, process all folders to add path information while preserving hierarchy
		const processedFolders = processAllFolders(projectFolders);

		// Then, flatten the tree for MUI DataGrid
		const allFolders = flattenFolders(processedFolders);

		// Sort the flattened folders
		const sortedFolders = [...allFolders].sort((a, b) => {
			const sortItem = sortModel[0];
			if (!sortItem) return 0;

			const aValue =
				sortItem.field === 'createdBy'
					? a[sortItem.field]?.name
					: a[sortItem.field];
			const bValue =
				sortItem.field === 'createdBy'
					? b[sortItem.field]?.name
					: b[sortItem.field];

			if (sortItem.sort === 'asc') {
				return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
			} else {
				return bValue < aValue ? -1 : bValue > aValue ? 1 : 0;
			}
		});

		// For each folder, check if it has subfolders to ensure proper tree display
		return sortedFolders.map((folder) => ({
			...folder,
			// For MUI DataGrid to show the expand arrow if there are subfolders
			hasSubfolders: hasFolderSubfolders(folder.id, allFolders),
		}));
	}, [projectFolders, sortModel]);

	// Handle sort model changes
	const handleSortModelChange = (model: GridSortModel) => {
		setSortModel(model);
	};

	return (
		<>
			<ProjectFolderCreationModal
				open={isSubFolderModalOpen}
				onClose={() => setIsSubFolderModalOpen(false)}
				folderPath={selectedFolderPath}
			/>
			{folderToEdit && (
				<EditProjectFolderModal
					open={isEditModalOpen}
					onClose={() => {
						setIsEditModalOpen(false);
						setFolderToEdit(null);
					}}
					folderId={folderToEdit.id}
					folderName={folderToEdit.name}
					folderPath={folderToEdit.folderPath}
				/>
			)}
			<Table
				columns={columns}
				rows={rows}
				page={0}
				pageSize={100}
				rowCount={rows.length}
				sortModel={sortModel}
				onSortModelChange={handleSortModelChange}
				onPaginationModelChange={() => {}}
				toolbar={<Toolbar />}
				loading={!initialized}
				boxSx={{
					height: 'calc(100vh - 215px)',
					backgroundColor: 'transparent',
				}}
				// Tree data props
				treeData={true}
				getTreeDataPath={getTreeDataPath}
				defaultGroupingExpansionDepth={1}
				getRowClassName={(params) => {
					// Add a custom class to child rows
					return isSubfolder(params.row.path) ? 'row-is-child' : '';
				}}
				groupingColDef={{
					headerName: 'Folder',
					field: 'display',
					minWidth: 325,
					disableColumnMenu: true,
					sortable: true,
					headerClassName: 'MuiDataGrid-columnHeader',
					hideDescendantCount: false,
					valueFormatter: (params) => {
						const foundFolder = rows.find(
							(folder) => folder.id === params
						);
						return foundFolder?.display || params.value;
					},
				}}
				initialState={{
					columns: {
						columnVisibilityModel: {
							// Hide id column and display column (as it's shown in the tree)
							id: false,
							display: false,
						},
					},
					sorting: {
						sortModel: [{ field: 'display', sort: 'asc' }],
					},
				}}
			/>
		</>
	);
}

// Menu component for row actions
const RowActionMenu = ({
	row,
	onEditFolder,
	onCreateSubFolder,
	onArchive,
	onUnarchive,
}: {
	row: FolderWithPath;
	onEditFolder: (id: string, name: string, folderPath?: string[]) => void;
	onCreateSubFolder: (folderPath: string[]) => void;
	onArchive: (id: string, folderPath?: string[]) => Promise<void>;
	onUnarchive: (id: string, folderPath?: string[]) => Promise<void>;
}) => {
	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
	const open = Boolean(anchorEl);
	const isSubFolder = row.path && row.path.length > 1;

	const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	return (
		<div>
			<IconButton onClick={handleClick} size="small" color="primary">
				<MoreHorizIcon />
			</IconButton>
			<Menu
				anchorEl={anchorEl}
				open={open}
				onClose={handleClose}
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'right',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
			>
				<MenuItem
					onClick={() => {
						onEditFolder(row.id, row.display, row.folderPath);
						handleClose();
					}}
				>
					<EditIcon
						fontSize="small"
						color="primary"
						style={{ marginRight: 8 }}
					/>
					Edit {isSubFolder ? 'Sub ' : ''}Folder Name
				</MenuItem>

				{/* Any folder can create a sub-folder when active */}
				{row.active && (
					<MenuItem
						onClick={() => {
							// For creating a sub-folder, we need the current folder's path plus its own id
							const folderPathForSubfolder = row.path;
							onCreateSubFolder(folderPathForSubfolder || []);
							handleClose();
						}}
					>
						<CreateNewFolderOutlinedIcon
							fontSize="small"
							color="primary"
							style={{ marginRight: 8 }}
						/>
						Create Sub Folder
					</MenuItem>
				)}

				{/* Archive/Unarchive option */}
				{row.active ? (
					<MenuItem
						onClick={() => {
							onArchive(row.id, row.folderPath);
							handleClose();
						}}
					>
						<DeleteOutlineOutlinedIcon
							fontSize="small"
							color="warning"
							style={{ marginRight: 8 }}
						/>
						Archive {isSubFolder ? 'Sub ' : ''}Folder
					</MenuItem>
				) : (
					<MenuItem
						onClick={() => {
							onUnarchive(row.id, row.folderPath);
							handleClose();
						}}
					>
						<UnarchiveOutlinedIcon
							fontSize="small"
							color="disabled"
							style={{ marginRight: 8 }}
						/>
						Unarchive {isSubFolder ? 'Sub ' : ''}Folder
					</MenuItem>
				)}
			</Menu>
		</div>
	);
};

const Toolbar = () => {
	const [isModalOpen, setIsModalOpen] = useState(false);

	return (
		<StyledGridToolbarContainer>
			<HelpText>
				Add or disable project folders to organize your projects. Expand
				folders to see and manage sub-folders. You can create
				sub-folders at any nesting level.
			</HelpText>
			<ButtonContainer>
				<AddToAstroButton onClick={() => setIsModalOpen(true)}>
					<AddIcon style={{ marginRight: 3 }} /> Add Project Folder
				</AddToAstroButton>
			</ButtonContainer>
			<ProjectFolderCreationModal
				open={isModalOpen}
				onClose={() => setIsModalOpen(false)}
			/>
		</StyledGridToolbarContainer>
	);
};

const ButtonContainer = styled.div`
	display: flex;
	gap: 10px;
`;

const AddToAstroButton = styled(Button)`
	text-transform: none !important;
	font-weight: normal !important;
	margin-top: 15px !important;
`;

const StyledGridToolbarContainer = styled(GridToolbarContainer)`
	display: block !important;
	padding: 0 !important;
`;

const HelpText = styled.div`
	font-size: 14px;
	color: ${(props) => props.theme.palette.text.secondary};
	margin-top: 15px;
	padding-left: 10px;
`;
